import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, ShoppingCart, Check, X } from 'lucide-react';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

export default function SuggestedOrders() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [brandFilter, setBrandFilter] = useState('');
  const [alertFilter, setAlertFilter] = useState('');
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [adjustments, setAdjustments] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (brandFilter) params.set('brand', brandFilter);
      if (alertFilter) params.set('alert', alertFilter);
      params.set('page', page);
      params.set('limit', 40);
      const result = await api.suggestedOrders(params.toString());
      setOrders(result.orders || []);
      setTotal(result.total || 0);
      setSummary(result.summary || {});
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => {
    api.brands().then(b => setBrands(b || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [page, brandFilter, alertFilter]);

  const handleExport = async () => {
    try {
      const blob = await api.exportOrders(brandFilter);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pedido_sugerido_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const handleAdjust = (productId, value) => {
    setAdjustments(prev => ({ ...prev, [productId]: value }));
  };

  return (
    <div>
      {/* Summary KPIs */}
      <div className="kpi-grid" style={{marginBottom:'20px'}}>
        <div className="kpi-card">
          <div className="kpi-label">Total Unidades Sugeridas</div>
          <div className="kpi-value">{(summary.total_suggested_units || 0).toLocaleString()}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Pedidos Urgentes</div>
          <div className="kpi-value danger">{summary.urgent || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Pedidos Sugeridos</div>
          <div className="kpi-value" style={{color:'hsl(199,84%,38%)'}}>{summary.suggested || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Sin Pedido</div>
          <div className="kpi-value success">{summary.no_order || 0}</div>
        </div>
      </div>

      <div className="data-table-container" data-testid="suggested-order-table">
        <div className="data-table-header">
          <h3>{total} productos</h3>
          <div className="data-table-actions">
            <select className="filter-select" value={brandFilter} onChange={e => { setBrandFilter(e.target.value); setPage(1); }}>
              <option value="">Todas las marcas</option>
              {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
            <select className="filter-select" value={alertFilter} onChange={e => { setAlertFilter(e.target.value); setPage(1); }}>
              <option value="">Todas las acciones</option>
              <option value="CRITICO">Urgente</option>
              <option value="BAJO">Bajo</option>
              <option value="NORMAL">Normal</option>
              <option value="SOBRESTOCK">Sobrestock</option>
            </select>
            <button className="btn btn-secondary" onClick={handleExport} data-testid="suggested-order-export-button">
              <Download size={14} /> Exportar CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : orders.length === 0 ? (
          <div className="empty-state"><ShoppingCart size={48} /><h3>Sin pedidos sugeridos</h3><p>Genera datos de ejemplo para ver pedidos sugeridos.</p></div>
        ) : (
          <>
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Marca</th>
                    <th>ABC</th>
                    <th className="right">Stock Neto</th>
                    <th className="right">V. Mes Ant.</th>
                    <th className="right">V. Año Ant.</th>
                    <th className="right">Trim. Pond.</th>
                    <th className="right">Cobertura</th>
                    <th className="right">Sugerido</th>
                    <th className="right">Ajuste</th>
                    <th>Acción</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.product_id}>
                      <td>
                        <div style={{fontWeight:500,fontSize:'0.8rem',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{o.name}</div>
                        <div style={{fontSize:'0.68rem',color:'hsl(var(--muted-foreground))'}}>{o.sku}</div>
                      </td>
                      <td style={{fontSize:'0.78rem'}}>{o.brand}</td>
                      <td><span className={`abc-badge ${o.abc_class}`}>{o.abc_class}</span></td>
                      <td className="right mono">{o.stock_neto}</td>
                      <td className="right mono">{o.V_MA}</td>
                      <td className="right mono">{o.V_AA}</td>
                      <td className="right mono">{o.trimestre_ponderado?.toFixed(0)}</td>
                      <td className="right">
                        <span style={{fontVariantNumeric:'tabular-nums',fontWeight:500}}>{o.coverage_days < 999 ? `${o.coverage_days.toFixed(0)}d` : '∞'}</span>
                      </td>
                      <td className="right mono" style={{fontWeight:700,fontSize:'0.9rem'}}>{o.pedido_sugerido}</td>
                      <td className="right">
                        <div className="editable-cell">
                          <input
                            type="number"
                            data-testid="suggested-order-adjustment-input"
                            value={adjustments[o.product_id] !== undefined ? adjustments[o.product_id] : ''}
                            onChange={e => handleAdjust(o.product_id, e.target.value)}
                            placeholder="-"
                            min="0"
                          />
                        </div>
                      </td>
                      <td><StatusBadge status={o.action} /></td>
                      <td>
                        <button className="btn btn-sm btn-secondary" data-testid="suggested-order-formula-drawer-open-button" onClick={() => setDrawer(o)}>
                          <Eye size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&lt;</button>
              <span style={{fontSize:'0.78rem',padding:'0 8px'}}>Página {page} de {Math.ceil(total / 40) || 1}</span>
              <button disabled={page >= Math.ceil(total / 40)} onClick={() => setPage(p => p + 1)}>&gt;</button>
            </div>
          </>
        )}
      </div>

      {/* Formula Breakdown Drawer */}
      {drawer && (
        <>
          <div className="drawer-overlay" onClick={() => setDrawer(null)} />
          <div className="drawer-panel">
            <div className="drawer-header">
              <h2>Desglose del Cálculo</h2>
              <button className="btn btn-sm btn-secondary" onClick={() => setDrawer(null)}><X size={14} /></button>
            </div>
            <div className="drawer-body">
              <div className="drawer-section">
                <h4>Producto</h4>
                <div className="drawer-row"><span className="label">Nombre</span><span className="value">{drawer.name}</span></div>
                <div className="drawer-row"><span className="label">SKU</span><span className="value">{drawer.sku}</span></div>
                <div className="drawer-row"><span className="label">Marca</span><span className="value">{drawer.brand}</span></div>
                <div className="drawer-row"><span className="label">Clase ABC</span><span className="value"><span className={`abc-badge ${drawer.abc_class}`}>{drawer.abc_class}</span></span></div>
              </div>

              <div className="drawer-section">
                <h4>Ventas Históricas</h4>
                <div className="drawer-row"><span className="label">Mes anterior (V_MA)</span><span className="value">{drawer.breakdown?.V_MA}</span></div>
                <div className="drawer-row"><span className="label">Mismo mes año anterior (V_AA)</span><span className="value">{drawer.breakdown?.V_AA}</span></div>
                <div className="drawer-row"><span className="label">Último trimestre (T1/T2/T3)</span><span className="value">{drawer.breakdown?.V_T1} / {drawer.breakdown?.V_T2} / {drawer.breakdown?.V_T3}</span></div>
                <div className="drawer-row"><span className="label">Trimestre ponderado</span><span className="value" style={{fontWeight:600}}>{drawer.breakdown?.trimestre_ponderado?.toFixed(1)}</span></div>
              </div>

              <div className="drawer-section">
                <h4>Factores de Ajuste</h4>
                <div className="drawer-row"><span className="label">Factor de tendencia</span><span className="value">{drawer.breakdown?.trend_factor?.toFixed(4)}</span></div>
                <div className="drawer-row"><span className="label">Factor estacional</span><span className="value">{drawer.breakdown?.seasonality_factor}</span></div>
                <div className="drawer-row"><span className="label">Ajuste de anomalía</span><span className="value">{drawer.breakdown?.anomaly_adjustment}</span></div>
                <div className="drawer-row"><span className="label">Demanda mensual ajustada</span><span className="value" style={{fontWeight:600}}>{drawer.breakdown?.demand_monthly?.toFixed(1)}</span></div>
              </div>

              <div className="drawer-section">
                <h4>Inventario</h4>
                <div className="drawer-row"><span className="label">Stock actual</span><span className="value">{drawer.breakdown?.current_stock}</span></div>
                <div className="drawer-row"><span className="label">En tránsito</span><span className="value">{drawer.breakdown?.in_transit}</span></div>
                <div className="drawer-row"><span className="label">Comprometido</span><span className="value">{drawer.breakdown?.committed}</span></div>
                <div className="drawer-row" style={{fontWeight:600}}><span className="label">Stock neto</span><span className="value">{drawer.breakdown?.stock_neto}</span></div>
              </div>

              <div className="drawer-section">
                <h4>Cálculo del Pedido</h4>
                <div className="drawer-row"><span className="label">Lead time</span><span className="value">{drawer.breakdown?.lead_time}d</span></div>
                <div className="drawer-row"><span className="label">Período de revisión</span><span className="value">{drawer.breakdown?.review_period}d</span></div>
                <div className="drawer-row"><span className="label">Horizonte</span><span className="value">{drawer.breakdown?.horizon}d</span></div>
                <div className="drawer-row"><span className="label">Demanda en horizonte</span><span className="value">{drawer.breakdown?.demand_horizon?.toFixed(1)}</span></div>
                <div className="drawer-row"><span className="label">Stock de seguridad</span><span className="value">{drawer.breakdown?.safety_stock}</span></div>
                <div className="drawer-row" style={{fontWeight:600}}><span className="label">Stock objetivo</span><span className="value">{drawer.breakdown?.stock_objetivo?.toFixed(0)}</span></div>
                <div className="drawer-row"><span className="label">Múltiplo de compra</span><span className="value">{drawer.breakdown?.purchase_multiple}</span></div>
                <div className="drawer-row"><span className="label">Cobertura actual</span><span className="value">{drawer.breakdown?.coverage_days?.toFixed(0)}d</span></div>
                <div style={{background:'hsl(var(--primary)/0.06)',padding:'12px',borderRadius:'8px',marginTop:'12px',textAlign:'center'}}>
                  <div style={{fontSize:'0.72rem',color:'hsl(var(--muted-foreground))',marginBottom:'4px'}}>PEDIDO SUGERIDO</div>
                  <div style={{fontFamily:'Space Grotesk',fontSize:'2rem',fontWeight:700,color:'hsl(var(--primary))'}}>{drawer.pedido_sugerido}</div>
                  <div style={{fontSize:'0.78rem',marginTop:'4px'}}><StatusBadge status={drawer.action} /></div>
                  <div style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))',marginTop:'6px'}}>{drawer.reason}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
